import AnalyticsTagContext from '@root/core/src/contexts/analytics-tag/analytics-tag-context';
import Layout from '@root/gatsby-contentful-core/src/components/layout';
import MixpanelDetails from '@root/gatsby-contentful-core/src/components/mixpanel-details';
import PropTypes from '@root/vendor/prop-types';
import Seo from '@root/gatsby-contentful-core/src/components/seo';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import useContentTagPageViewEvents from '@root/core/src/hooks/use-content-tag-page-view-events';
import useRegisterAgentAttribution from '@root/attribution/src/hooks/use-register-agent-attribution';
import useSaveAttribution from '@root/attribution/src/hooks/use-save-attribution';
import useSaveTouchAttributionEffect from '@root/attribution/src/hooks/use-save-touch-attribution-effect';
import { useMemo } from '@root/vendor/react';

function CorePageTemplateBody({
  analyticsContext,
  analyticsTag,
  children,
  ctaLink,
  ctaText,
  deIndexPage,
  footer,
  includeClarity,
  includeOptimizely,
  isCtaButtonDisabled,
  isStickyFooterDisabled,
  location,
  mainStyles,
  nav,
  pageCanonical,
  pageDescription,
  pageThumbnail,
  pageTitle,
  isDarkMode,
}) {
  useAnalytics(analyticsContext);
  useContentTagPageViewEvents(analyticsTag, pageTitle);
  useSaveAttribution(location);
  useSaveTouchAttributionEffect();
  useRegisterAgentAttribution();

  const analyticsTagValue = useMemo(() => ({
    analyticsTag,
    pageTitle,
  }), [analyticsTag, pageTitle]);

  return (
    <AnalyticsTagContext.Provider value={analyticsTagValue}>
      <Layout
        ctaLink={ctaLink}
        ctaText={ctaText}
        footer={footer}
        includeClarity={includeClarity}
        includeOptimizely={includeOptimizely}
        isCtaButtonDisabled={isCtaButtonDisabled}
        isDarkMode={isDarkMode}
        isStickyFooterDisabled={isStickyFooterDisabled}
        location={location}
        mainStyles={mainStyles}
        nav={nav}
      >
        <Seo
          canonical={pageCanonical}
          deIndexPage={deIndexPage}
          description={pageDescription}
          image={pageThumbnail}
          title={pageTitle}
        />

        {analyticsContext && (
          <MixpanelDetails
            analyticsContext={analyticsContext}
            isPageView={true}
          />
        )}
        {children}
      </Layout>
    </AnalyticsTagContext.Provider>
  );
}

CorePageTemplateBody.propTypes = {
  analyticsContext: PropTypes.string,
  analyticsTag: PropTypes.string,
  children: PropTypes.any,
  ctaLink: PropTypes.string,
  ctaText: PropTypes.string,
  deIndexPage: PropTypes.bool,
  footer: PropTypes.func.isRequired,
  includeClarity: PropTypes.bool,
  includeOptimizely: PropTypes.bool,
  isCtaButtonDisabled: PropTypes.bool,
  isDarkMode: PropTypes.bool,
  isStickyFooterDisabled: PropTypes.bool,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  mainStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
  nav: PropTypes.func.isRequired,
  pageCanonical: PropTypes.string.isRequired,
  pageDescription: PropTypes.string,
  pageThumbnail: PropTypes.string,
  pageTitle: PropTypes.string.isRequired,
};

export default function CorePageTemplate({
  analyticsContext,
  analyticsTag,
  children,
  ctaLink,
  ctaText,
  deIndexPage,
  footer,
  includeClarity,
  includeOptimizely,
  isCtaButtonDisabled,
  isStickyFooterDisabled,
  location,
  mainStyles,
  nav,
  pageCanonical,
  pageDescription,
  pageThumbnail,
  pageTitle,
  isDarkMode,
}) {
  return (
    <CorePageTemplateBody
      analyticsContext={analyticsContext}
      analyticsTag={analyticsTag}
      ctaLink={ctaLink}
      ctaText={ctaText}
      deIndexPage={deIndexPage}
      footer={footer}
      includeClarity={includeClarity}
      includeOptimizely={includeOptimizely}
      isCtaButtonDisabled={isCtaButtonDisabled}
      isDarkMode={isDarkMode}
      isStickyFooterDisabled={isStickyFooterDisabled}
      location={location}
      mainStyles={mainStyles}
      nav={nav}
      pageCanonical={pageCanonical}
      pageDescription={pageDescription}
      pageThumbnail={pageThumbnail}
      pageTitle={pageTitle}
    >
      {children}
    </CorePageTemplateBody>
  );
}

CorePageTemplate.propTypes = {
  analyticsContext: PropTypes.string,
  analyticsTag: PropTypes.string,
  children: PropTypes.any,
  ctaLink: PropTypes.string,
  ctaText: PropTypes.string,
  deIndexPage: PropTypes.bool,
  footer: PropTypes.func.isRequired,
  includeClarity: PropTypes.bool,
  includeOptimizely: PropTypes.bool,
  isCtaButtonDisabled: PropTypes.bool,
  isDarkMode: PropTypes.bool,
  isStickyFooterDisabled: PropTypes.bool,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  mainStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
  nav: PropTypes.func.isRequired,
  pageCanonical: PropTypes.string.isRequired,
  pageDescription: PropTypes.string,
  pageThumbnail: PropTypes.string,
  pageTitle: PropTypes.string.isRequired,
};
